import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useCallIntelSubscription } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, Text, useTooltip } from '@frontend/design-system';
import { useCallIntelLocations } from './hooks/use-call-intel-locations';

type CallIntelTrialHeaderChipsProps = {
  locationIds?: string[];
};

export const CallIntelTrialHeaderChips = ({ locationIds }: CallIntelTrialHeaderChipsProps) => {
  const { t } = useTranslation('call-intelligence');
  const { locations } = useCallIntelLocations();
  const { trialActiveLocationIds } = useCallIntelSubscription({ locationIds });
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({
    placement: 'bottom-start',
  });

  const selectedTrialLocationIds = useMemo(() => {
    if (!locationIds?.length) {
      return trialActiveLocationIds;
    }

    const selectedIds = new Set(locationIds);
    return trialActiveLocationIds.filter((locationId) => selectedIds.has(locationId));
  }, [locationIds, trialActiveLocationIds]);

  const selectedTrialLocationNames = useMemo(() => {
    return selectedTrialLocationIds.map((locationId) => locations[locationId] || locationId);
  }, [locations, selectedTrialLocationIds]);

  if (selectedTrialLocationIds.length === 0) {
    return null;
  }

  return (
    <div css={styles.wrapper} data-testid='call-intel-trial-header-chips'>
      <Chip variant='seaweed' leftElement={<Icon name='award-small' />} css={styles.freeTrialChip}>
        {t('Free Trial')}
      </Chip>

      <div {...triggerProps}>
        <Chip
          variant='neutral'
          leftElement={<Icon name='locations-small' />}
          css={styles.locationCountChip}
          data-testid='call-intel-trial-location-count-chip'
        >
          {selectedTrialLocationIds.length}
        </Chip>
      </div>

      <Tooltip {...tooltipProps} css={styles.tooltip}>
        <Text as='div' size='medium' color='white'>
          {t('Locations on Free Trial:')}
        </Text>

        <ul css={styles.tooltipList}>
          {selectedTrialLocationNames.map((locationName) => (
            <li key={locationName}>
              <Text as='span' size='medium' color='white'>
                {locationName}
              </Text>
            </li>
          ))}
        </ul>
      </Tooltip>
    </div>
  );
};

const styles = {
  wrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};
  `,
  freeTrialChip: css`
    gap: ${theme.spacing(0.5)};
    padding: ${theme.spacing(0.25, 0.75)};
  `,
  locationCountChip: css`
    gap: ${theme.spacing(0.5)};
    cursor: default;
    padding: ${theme.spacing(0.25, 0.75)};
  `,
  tooltip: css`
    max-width: 420px;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1.5, 2)};
  `,
  tooltipList: css`
    margin: 0;
    padding-left: ${theme.spacing(2)};
  `,
};
